import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Form, FormFeedback, UncontrolledTooltip, Input, Label, Row, Modal, ModalHeader, ModalBody } from "reactstrap";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import ImageCropper from "../../../components/Common/ImageCropper";
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";

import * as Yup from "yup";
import { useFormik } from "formik";
import moment from "moment";
import { createEventList, getAllEventCategory } from "../../../api";
import Select from "react-select";
import { eventTime } from "utils/constants";
import toast, { Toaster } from "react-hot-toast";

const CreateListEvent = () => {
  document.title = "Create Event | Eventox";

  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEventTime, setSelectedEventTime] = useState(null);
  const [selectedThumbImg, setSelectedThumbImg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedCropFile, setSelectedCropFile] = useState(null);
  const [cropField, setCropField] = useState(null);

  useEffect(() => {
    if (localStorage.getItem("authUser")) {
      try {
        const adminData = JSON.parse(localStorage.getItem("authUser"));
  
        if (adminData.user.userId) {
          const userId = Number(adminData.user.userId);
  
          if (Number.isInteger(userId)) {
            setCurrentUserId(userId);
          } else {
            console.error("Invalid userId in authUser:", userId);
          }
        }
      } catch (err) {
        console.error("Failed to parse authUser", err);
      }
    }
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const respCategory = await getAllEventCategory();
        const categoryOption = respCategory.map((cat) => ({
          value: cat.categoryId,
          label: cat.categoryName,
        }));
        setCategoryList(categoryOption);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);

  const handleImageChange = (event, field) => {
    if (event.target.files[0].size > 2097152) {
      toast.error("File size should be less than 2MB");
    } else {
      event.preventDefault();
      let file = event.target.files[0];
      setSelectedCropFile(file);
      setCropField(field);
      setIsCropperOpen(true);
      event.target.value = '';
    }
  };

  const validation = useFormik({
    initialValues: {
      eventName: "",
      shortDescription: "",
      thumbImg: "",
      categoryId: "",
      eventDate: "",
      eventTime: "",
      isFeatured: "false",
      eventCreatedBy: currentUserId,
      organizerName: "",
      eventUrl: "",
      eventLocation: "",
    },
    validationSchema: Yup.object({
      eventName: Yup.string(),
      shortDescription: Yup.string(),
      categoryId: Yup.string(),
      eventDate: Yup.string(),
      eventTime: Yup.string(),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
    
      const formData = new FormData();
    
      formData.append("event_name", values.eventName);
      formData.append("event_description", values.shortDescription);
      formData.append("event_date", values.eventDate);
      formData.append("event_time", values.eventTime);
      formData.append("event_category", values.categoryId);
      formData.append("event_status", "active");
      formData.append("is_active", "true");
      formData.append("is_featured", values.isFeatured);
      formData.append("event_created_by", String(currentUserId));
      formData.append("organizer_name", values.organizerName || "");
      formData.append("event_url", values.eventUrl || "");
      formData.append("event_location", values.eventLocation || "");
    
      if (values.thumbImg) {
        formData.append("file", values.thumbImg);
      }
    
      try {
        await createEventList(formData);
        resetFields();
        toast.success(`${values.eventName} event created successfully`);
      } catch (error) {
        toast.error("Failed to create event");
      } finally {
        setIsLoading(false);
      }
    }
  });

  const resetFields = () => {
    validation.resetForm();
    setSelectedThumbImg(null);
    setSelectedCategory(null);
    setSelectedEventTime(null);
  };

  return (
    <React.Fragment>
      <Toaster />
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Events" breadcrumbItem="Create Event" />
          <Form
            id="createproject-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <input type="hidden" className="form-control" id="formAction" name="formAction" defaultValue="add" />
                    <input type="hidden" className="form-control" id="project-id-input" />

                    {/* Event Name */}
                    <div className="mb-3">
                      <Label htmlFor="eventName">Event Name</Label>
                      <Input
                        id="eventName"
                        name="eventName"
                        type="text"
                        placeholder="Enter Event Name..."
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.eventName || ""}
                      />
                    </div>

                    {/* Short Description */}
                    <div className="mb-3">
                      <Label htmlFor="shortDescription">Short Description</Label>
                      <Input
                        type="textarea"
                        id="shortDescription"
                        name="shortDescription"
                        rows={1}
                        placeholder="Enter Event Short Description..."
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.shortDescription || ""}
                      />
                    </div>

                    {/* Organizer Name & Event URL */}
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="organizerName">Organizer Name</Label>
                          <Input
                            id="organizerName"
                            name="organizerName"
                            type="text"
                            placeholder="Enter Organizer Name..."
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.organizerName || ""}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="eventUrl">Event URL</Label>
                          <Input
                            id="eventUrl"
                            name="eventUrl"
                            type="url"
                            placeholder="https://..."
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.eventUrl || ""}
                          />
                        </div>
                      </Col>
                    </Row>
                    <div className="mb-3">
                      <Label htmlFor="eventLocation">Event Location</Label>
                      <Input
                        id="eventLocation"
                        name="eventLocation"
                        type="text"
                        placeholder="Enter Event Location..."
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.eventLocation || ""}
                      />
                    </div>
                    <Row>
                      {/* Event Date */}
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Event Date</Label>
                          <FlatPickr
                            className="form-control"
                            name="eventDate"
                            placeholder="Select date"
                            options={{
                              mode: "single",
                              dateFormat: "Y-m-d",
                            }}
                            onChange={(date) =>
                              validation.setFieldValue(
                                "eventDate",
                                moment(date[0]).format("YYYY-MM-DD")
                              )
                            }
                            value={validation.values.eventDate || ""}
                          />
                        </div>
                      </Col>

                      {/* Event Time */}
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Event Time</Label>
                          <Select
                            value={selectedEventTime}
                            placeholder="Select Time"
                            onChange={(data) => {
                              setSelectedEventTime(data);
                              validation.setFieldValue("eventTime", data.value);
                            }}
                            options={eventTime}
                            className="select2-selection"
                          />
                        </div>
                      </Col>
                    </Row>

                     {/* Event Category */}
                    <div className="mb-3" style={{ width: "30%" }}>
                      <Label>Event Category</Label>
                      <Select
                        value={selectedCategory}
                        placeholder="Select Category"
                        onChange={(data) => {
                          setSelectedCategory(data);
                          validation.setFieldValue("categoryId", data.value);
                        }}
                        options={categoryList}
                        width="100%"
                        className="select2-selection"
                      />
                    </div>

                    {/* Thumbnail Image */}
                    <Row>
                      <Col lg={4}>
                        <div className="mb-3">
                          <Label className="form-label">Thumbnail Image</Label>
                          <p className="text-muted m-b-15">The image should be min 561 × 691 pixel</p>
                          <div className="">
                            <div className="position-relative d-inline-block">
                              <div className="position-absolute bottom-0 end-0">
                                <Label htmlFor="project-image-input1" className="mb-0" id="projectImageInput">
                                  <div className="avatar-xs">
                                    <div className="avatar-title bg-light border rounded-circle text-muted cursor-pointer shadow font-size-16">
                                      <i className="bx bxs-image-alt"></i>
                                    </div>
                                  </div>
                                </Label>
                                <UncontrolledTooltip placement="right" target="projectImageInput">
                                  Select Image
                                </UncontrolledTooltip>
                                <input
                                  className="form-control d-none"
                                  id="project-image-input1"
                                  type="file"
                                  accept="image/png, image/gif, image/jpeg"
                                  onChange={(e) => handleImageChange(e, "thumbImg")}
                                />
                              </div>
                              <div className="avatar-lg upload-image-container">
                                <div className="avatar-title bg-light">
                                  <img src={selectedThumbImg || ""} id="projectlogo-img" alt="" className="avatar-md h-auto" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Is Featured */}
                    <div className="mb-3" style={{ width: "30%" }}>
                      <Label htmlFor="isFeatured">Is Featured Event</Label>
                      <div className="form-check form-switch mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="isFeatured"
                          checked={validation.values.isFeatured === "true"}
                          onChange={(e) => {
                            validation.setFieldValue("isFeatured", e.target.checked ? "true" : "false");
                          }}
                          style={{ width: "40px", height: "20px", cursor: "pointer" }}
                        />
                        <label className="form-check-label ms-2" htmlFor="isFeatured">
                          Is Featured
                        </label>
                      </div>
                    </div>

                    {/* Submit */}
                    <Col lg={12}>
                      <div className="text-end ">
                        <Button type="submit" disabled={isLoading} color="primary">
                          {isLoading ? "Creating event..." : "Create Event"}
                        </Button>
                      </div>
                    </Col>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>

      <Modal isOpen={isCropperOpen} toggle={() => setIsCropperOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsCropperOpen(false)}>Crop Image</ModalHeader>
        <ModalBody>
          {selectedCropFile && (
            <ImageCropper
              imageFile={selectedCropFile}
              onCropComplete={(croppedFile) => {
                validation.setFieldValue(cropField, croppedFile);
                const reader = new FileReader();
                reader.onloadend = () => {
                  if (cropField === "thumbImg") setSelectedThumbImg(reader.result);
                };
                reader.readAsDataURL(croppedFile);
                setIsCropperOpen(false);
              }}
            />
          )}
        </ModalBody>
      </Modal>

    </React.Fragment>
  );
};

export default CreateListEvent;